/**
 * The Today strings (pedagogy spec §6.4) and the two "Nästa steg" / summary strings of the same
 * step, kept apart from the rest of the practice dictionary. Swedish says "moment" for the
 * course's chapters and "idag"; English says "module".
 */
import type { LocalizedDictionary } from "@client/i18n";

export const todayDict: LocalizedDictionary = {
  sv: {
    "practice.today.title": "Dagens pass",
    "practice.today.description":
      "Byggt av det som ska repeteras, det du missade tidigare och ett ämne du inte har börjat på än.",
    "practice.today.minutes": "≈ {minutes} min",
    "practice.today.stepsLabel": "Steg i dagens pass",
    "practice.today.step.cards.title": "Uppvärmning: kort",
    "practice.today.step.cards.count.one": "{count} kort",
    "practice.today.step.cards.count.other": "{count} kort",
    "practice.today.step.cards.body.one": "{cards} från moment {chapters}.",
    "practice.today.step.cards.body.other": "{cards}, blandat från moment {chapters}.",
    "practice.today.step.retest.title.one": "Repetition: {count} uppgift du missade",
    "practice.today.step.retest.title.other": "Repetition: {count} uppgifter du missade",
    "practice.today.step.retest.body":
      "Uppgifter du missade för minst två dagar sedan. Du får en ny variant eller en annan uppgift i samma ämne, aldrig samma uppgift igen.",
    "practice.today.step.retest.titleAll.one": "Repetition: {count} uppgift",
    "practice.today.step.retest.titleAll.other": "Repetition: {count} uppgifter",
    "practice.today.step.retest.bodyAll":
      "Först det du har missat, sedan ämnen du klarade på egen hand för några dagar sedan. Du får en ny variant eller en annan uppgift i samma ämne, aldrig samma uppgift igen.",
    "practice.today.step.new.title": "Nytt: {topic}",
    "practice.today.step.new.prepare": "Förbered {id}: {topic}",
    "practice.today.step.new.catchUp": "Ta igen {id}: {topic}",
    "practice.today.step.new.worked":
      "Studera ett löst exempel, gör klart ett och lös sedan ett på egen hand.",
    "practice.today.step.new.practice.one": "{count} uppgift att börja med.",
    "practice.today.step.new.practice.other":
      "{count} uppgifter att börja med, den lättaste först.",
    "practice.today.step.new.startedToday": "Du började på det här ämnet idag.",
    "practice.today.badge.retrieval": "Hämta ur minnet",
    "practice.today.badge.mixed": "Blandade moment",
    "practice.today.badge.spaced.one": "Efter {count} dag",
    "practice.today.badge.spaced.other": "Efter {count} dagar",
    "practice.today.badge.worked": "Exempel → egen lösning",
    "practice.today.badge.firstProblems": "Första uppgifterna",
    "practice.today.start": "Starta passet",
    "practice.today.continue": "Fortsätt passet",
    "practice.today.adjust": "Anpassa",
    "practice.today.stopAnytime": "Du kan sluta efter vilket steg som helst.",
    "practice.today.status.done": "Klart",
    "practice.today.status.skipped": "Överhoppat",
    "practice.today.status.current": "Pågår",
    "practice.today.status.pending": "Kvar",
    "practice.today.nextStep": "Nästa steg",
    "practice.today.skipStep": "Hoppa över det här steget",
    "practice.today.stopHere": "Sluta här för idag",
    "practice.today.doAnyway": "Gör ändå",
    "practice.today.empty": "Inget planerat idag. Öva fritt, repetera kort eller skriv en tenta.",
    "practice.today.finished": "Dagens pass är klart.",
    "practice.today.adjust.title": "Anpassa dagens pass",
    "practice.today.adjust.includeCards": "Ta med kort",
    "practice.today.adjust.cardLimit": "Högst antal kort",
    "practice.today.adjust.retests": "Antal uppgifter att repetera",
    "practice.today.adjust.includeNew": "Ta med ett nytt ämne",
    "practice.today.adjust.newTopic": "Nytt ämne",
    "practice.today.adjust.newTopicAuto": "Nästa i kursens ordning",
    "practice.today.adjust.newTopicLecture": "Nästa föreläsning ({id})",
    "practice.today.adjust.newTopicCatchUp": "Ta igen föreläsningarna ({id})",
    "practice.today.adjust.topicOption": "Moment {chapter} · {topic}",
    "practice.today.adjust.skipTopic": "Hoppa över det här ämnet framöver",
    "practice.today.adjust.skipTopicHint":
      "Gäller {topic}. Du kan alltid välja det för hand under Nytt ämne.",
    "practice.today.adjust.fullForm": "Välj moment och uppgiftstyper själv",
    "practice.today.evidence.title": "Så går det",
    "practice.today.evidence.topics": "Påbörjade ämnen",
    "practice.today.evidence.topicsValue": "{started} av {total}",
    "practice.today.evidence.unaided": "Lösta utan hjälp, senaste 7 dagarna",
    "practice.today.evidence.retests": "Klarade repetitioner, senaste 7 dagarna",
    "practice.today.evidence.retestsValue": "{passed} av {taken}",
    "practice.today.evidence.open": "Se alla framsteg",
    "practice.today.evidence.cards": "Repeterade kort, senaste 7 dagarna",
    "practice.today.tally": "Idag: {cards}, {problems}",
    "practice.today.tally.cards.one": "{count} kort",
    "practice.today.tally.cards.other": "{count} kort",
    "practice.today.tally.problems.one": "{count} uppgift",
    "practice.today.tally.problems.other": "{count} uppgifter",
    "practice.today.mathSupport": "Känns matten osäker? Börja gärna här:",
    "practice.today.mathSupport.help": "Så skriver du svar",
    "practice.summary.backToToday": "Fortsätt med dagens pass",
    "practice.next.title": "Nästa steg",
    "practice.next.comesBack":
      "Kommer tillbaka {date} som en ny variant eller en annan uppgift i samma ämne.",
    "practice.next.comesBackBadge": "Utspridd repetition",
  },
  en: {
    "practice.today.title": "Today's session",
    "practice.today.description":
      "Built from what is due, what you missed earlier and one topic you have not started yet.",
    "practice.today.minutes": "≈ {minutes} min",
    "practice.today.stepsLabel": "Steps in today's session",
    "practice.today.step.cards.title": "Warm-up: flashcards",
    "practice.today.step.cards.count.one": "{count} card",
    "practice.today.step.cards.count.other": "{count} cards",
    "practice.today.step.cards.body.one": "{cards} from module {chapters}.",
    "practice.today.step.cards.body.other": "{cards}, mixed from modules {chapters}.",
    "practice.today.step.retest.title.one": "Re-test: {count} problem you missed",
    "practice.today.step.retest.title.other": "Re-test: {count} problems you missed",
    "practice.today.step.retest.body":
      "Problems you missed at least two days ago. You get a new variant or another problem on the same topic, never the same problem again.",
    "practice.today.step.retest.titleAll.one": "Re-test: {count} problem",
    "practice.today.step.retest.titleAll.other": "Re-test: {count} problems",
    "practice.today.step.retest.bodyAll":
      "What you missed comes first, then topics you solved on your own a few days ago. You get a new variant or another problem on the same topic, never the same problem again.",
    "practice.today.step.new.title": "New: {topic}",
    "practice.today.step.new.prepare": "Prepare {id}: {topic}",
    "practice.today.step.new.catchUp": "Catch up on {id}: {topic}",
    "practice.today.step.new.worked":
      "Study one worked example, complete one, then solve one on your own.",
    "practice.today.step.new.practice.one": "{count} problem to start with.",
    "practice.today.step.new.practice.other": "{count} problems to start with, easiest first.",
    "practice.today.step.new.startedToday": "You started this topic today.",
    "practice.today.badge.retrieval": "Retrieval",
    "practice.today.badge.mixed": "Mixed modules",
    "practice.today.badge.spaced.one": "After {count} day",
    "practice.today.badge.spaced.other": "After {count} days",
    "practice.today.badge.worked": "Worked example → your own",
    "practice.today.badge.firstProblems": "First problems",
    "practice.today.start": "Start session",
    "practice.today.continue": "Continue session",
    "practice.today.adjust": "Adjust",
    "practice.today.stopAnytime": "You can stop after any step.",
    "practice.today.status.done": "Done",
    "practice.today.status.skipped": "Skipped",
    "practice.today.status.current": "In progress",
    "practice.today.status.pending": "To do",
    "practice.today.nextStep": "Next step",
    "practice.today.skipStep": "Skip this step",
    "practice.today.stopHere": "Stop here for today",
    "practice.today.doAnyway": "Do it anyway",
    "practice.today.empty":
      "Nothing planned for today. Practise freely, review cards or sit an exam.",
    "practice.today.finished": "Today's session is done.",
    "practice.today.adjust.title": "Adjust today's session",
    "practice.today.adjust.includeCards": "Include flashcards",
    "practice.today.adjust.cardLimit": "Maximum number of cards",
    "practice.today.adjust.retests": "Number of problems to re-test",
    "practice.today.adjust.includeNew": "Include a new topic",
    "practice.today.adjust.newTopic": "New topic",
    "practice.today.adjust.newTopicAuto": "Next in course order",
    "practice.today.adjust.newTopicLecture": "Next lecture ({id})",
    "practice.today.adjust.newTopicCatchUp": "Catch up on the lectures ({id})",
    "practice.today.adjust.topicOption": "Module {chapter} · {topic}",
    "practice.today.adjust.skipTopic": "Skip this topic from now on",
    "practice.today.adjust.skipTopicHint":
      "Applies to {topic}. You can always pick it by hand under New topic.",
    "practice.today.adjust.fullForm": "Choose modules and question types yourself",
    "practice.today.evidence.title": "How it is going",
    "practice.today.evidence.topics": "Topics started",
    "practice.today.evidence.topicsValue": "{started} of {total}",
    "practice.today.evidence.unaided": "Solved without help, last 7 days",
    "practice.today.evidence.retests": "Re-tests passed, last 7 days",
    "practice.today.evidence.retestsValue": "{passed} of {taken}",
    "practice.today.evidence.open": "See all progress",
    "practice.today.evidence.cards": "Cards reviewed, last 7 days",
    "practice.today.tally": "Today: {cards}, {problems}",
    "practice.today.tally.cards.one": "{count} card",
    "practice.today.tally.cards.other": "{count} cards",
    "practice.today.tally.problems.one": "{count} problem",
    "practice.today.tally.problems.other": "{count} problems",
    "practice.today.mathSupport": "Unsure about the maths? Start here:",
    "practice.today.mathSupport.help": "How to write answers",
    "practice.summary.backToToday": "Continue today's session",
    "practice.next.title": "What next",
    "practice.next.comesBack":
      "Comes back on {date} as a new variant or another problem on the same topic.",
    "practice.next.comesBackBadge": "Spaced re-test",
  },
};
